import DOMPurify from 'dompurify'
import { Marked } from 'marked'

const marked = new Marked({
  gfm: true,
  breaks: false,
})

// Force every outbound link to open safely. Even though only the admin can
// author content, a stolen session shouldn't translate into a reverse-tabnabbing
// vector against readers.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node instanceof HTMLAnchorElement && node.hasAttribute('href')) {
    const href = node.getAttribute('href') ?? ''
    if (/^https?:\/\//i.test(href)) {
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener noreferrer')
    }
  }
})

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'hr', 'strong', 'em', 'del', 'code', 'pre', 'blockquote',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li', 'a', 'img', 'figure', 'figcaption',
    'table', 'thead', 'tbody', 'tr', 'th', 'td', 'sup', 'sub',
  ],
  ALLOWED_ATTR: ['href', 'title', 'src', 'alt', 'width', 'height', 'target', 'rel', 'loading'],
  ALLOW_DATA_ATTR: false,
  FORBID_TAGS: ['style', 'script', 'iframe', 'form', 'input'],
}

/**
 * Markdown → sanitized HTML, ready for dangerouslySetInnerHTML.
 *
 * `timestamps` turns `[0:42]` into a link that seeks the recording. Only the
 * transcript asks for it — the overview is prose, where a bare `4:07` is far
 * more likely to be a figure than a cue.
 */
export function renderMarkdown(markdown: string, { timestamps = false } = {}): string {
  const source = timestamps ? linkTimestamps(markdown ?? '') : (markdown ?? '')
  const raw = marked.parse(source, { async: false }) as string
  return DOMPurify.sanitize(raw, SANITIZE_CONFIG) as unknown as string
}

/**
 * Markdown → sanitized HTML with no block wrapper.
 *
 * The transcript renders one React element per line, so the text of a line has
 * already been given its own container — running it through the block parser
 * would nest a <p> inside that and break the layout.
 */
export function renderMarkdownInline(markdown: string, { timestamps = false } = {}): string {
  const source = timestamps ? linkTimestamps(markdown ?? '') : (markdown ?? '')
  const raw = marked.parseInline(source, { async: false }) as string
  return DOMPurify.sanitize(raw, SANITIZE_CONFIG) as unknown as string
}

/**
 * Timestamps in a transcript become links that seek the recording.
 *
 * Only `[0:42]`, `(0:42)` and a bare `0:42` at the very start of a line are
 * matched. Bare times mid-sentence are left alone on purpose — "we argued for
 * 3:45 straight" is prose, not a cue, and turning it into a seek link would be
 * both wrong and unclickable-looking.
 */
/**
 * A timestamp is either bracketed, or the first thing on a line — optionally
 * after markdown list/quote punctuation, so `- 0:42` and `> 0:42` still count.
 * `(\]|\)|(?![\d:]))` closes it: a matching bracket, or simply nothing that
 * would make it part of a longer number.
 */
const TIMESTAMP = /(^[\s>*-]*|\[|\()((?:(\d{1,2}):)?(\d{1,2}):([0-5]\d))(\]|\)|(?![\d:]))/g

const toSeconds = (h: string | undefined, m: string, s: string) =>
  Number(h ?? 0) * 3600 + Number(m) * 60 + Number(s)

export function linkTimestamps(markdown: string): string {
  let inFence = false
  return (markdown ?? '')
    .split('\n')
    .map((line) => {
      // Never rewrite inside a code fence — a timestamp there is sample data.
      if (/^\s*```/.test(line)) {
        inFence = !inFence
        return line
      }
      if (inFence) return line

      return line.replace(TIMESTAMP, (match, lead: string, stamp: string, h: string, m: string, s: string, close: string) => {
        const bracketed = lead === '[' || lead === '('
        // An opening bracket has to be closed by its own partner, or this is
        // something else entirely and should be left exactly as written.
        if (bracketed && close !== (lead === '[' ? ']' : ')')) return match

        const link = `[${stamp}](#t=${toSeconds(h, m, s)})`
        // The brackets themselves are swallowed — the link gets styled as a chip
        // on the page, and `[[0:42](#t=42)]` would render those as literal text.
        return bracketed ? link : `${lead}${link}`
      })
    })
    .join('\n')
}
